import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { showToast } from '../hooks/useToast';
import ThemeToggle from '../components/UI/ThemeToggle';
import LanguageSelector from '../components/UI/LanguageSelector';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/auth/forgot-password', { email });
    } catch {
      showToast(t('login.errorServer'), 'error');
    } finally {
      setLoading(false);
      // AC1: always show success regardless of outcome (anti-enumeration)
      setSubmitted(true);
    }
  }

  return (
    <main className="flex min-h-screen">
      {/* LEFT — hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        {/* Decorative rings */}
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />

        <div className="relative">
          <div className="mb-6">
            <LockIcon />
          </div>
          <h1 className="font-display text-[56px] leading-[64px] font-semibold text-white tracking-[-0.01em]">
            {t('forgotPassword.heroTitle')}
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;{t('forgotPassword.heroSubtitle')}&rdquo;
          </p>
        </div>

        <div className="absolute left-16 right-16 bottom-14 flex flex-col gap-3.5 text-white/90 text-sm">
          <div className="flex items-center gap-3">
            <ShieldIcon />
            <span>{t('forgotPassword.hint1')}</span>
          </div>
          <div className="flex items-center gap-3">
            <ClockIcon />
            <span>{t('forgotPassword.hint2')}</span>
          </div>
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">

        {/* Controls */}
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        {/* Mobile brand */}
        <div className="md:hidden flex flex-col items-center pt-20 pb-6 px-6">
          <span className="font-display text-[32px] font-semibold text-primary-600 tracking-[-0.01em]">
            Udbodhanam
          </span>
          <p className="font-display italic text-text-muted text-[15px] font-medium mt-1 text-center">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>

        {/* Form area */}
        <div className="flex-1 flex flex-col md:justify-center px-6 pb-10 pt-4 md:px-[72px] md:py-[60px]">
          <div className="max-w-[440px] w-full mx-auto">

            {submitted ? (
              /* Success state */
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-growth-50 border border-growth-100 mb-6">
                  <MailCheckIcon />
                </div>
                <h2 className="text-[28px] font-bold text-earth-800 tracking-[-0.015em] mb-3">
                  {t('forgotPassword.successTitle')}
                </h2>
                <p className="text-earth-600 text-sm leading-relaxed mb-2">
                  {t('forgotPassword.successMessage')}
                </p>
                <p className="text-earth-500 text-xs leading-relaxed mb-8">
                  {t('forgotPassword.successNote')}
                </p>
                <Link
                  to="/login"
                  className="inline-block text-sm text-primary-600 hover:underline"
                >
                  {t('forgotPassword.backToSignIn')}
                </Link>
              </div>
            ) : (
              /* Form state */
              <>
                <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
                  {t('forgotPassword.title')}
                </h2>
                <p className="text-earth-600 mt-1.5 text-sm">
                  {t('forgotPassword.subtitle')}
                </p>

                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                      {t('forgotPassword.emailLabel')}<span className="ml-0.5">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full h-[42px] border border-border rounded-lg px-[14px] text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 placeholder:text-earth-400"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700 disabled:opacity-50"
                  >
                    {loading ? t('forgotPassword.sending') : t('forgotPassword.submit')}
                  </button>
                </form>

                <p className="text-center mt-6 text-sm">
                  <Link to="/login" className="text-primary-600 hover:underline">
                    {t('forgotPassword.backToSignIn')}
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function LockIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function MailCheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4FAE6A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9" />
      <path d="m2 6 10 7 10-7" />
      <path d="m16 19 2 2 4-4" />
    </svg>
  );
}
